import React from 'react';
import { Row, Col, Button } from 'antd';
import { FileAddOutlined } from '@ant-design/icons';

import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import PhotoReel from '../../../common/photos/PhotoReel';

import TitleRow from './TitleRow';

import FieldTriggerCheckbox from './FieldTriggerCheckbox';
import FieldTriggerFlag from './FieldTriggerFlag';

import colors from '../../../constants/Colors';
import ConditionalRenderingRow from './ConditionalRenderingRow';

import FileTileView from '../../../common/files/FileTileView/FileTileView';

const resolveStaticFileId = (file) => (typeof file === 'string' ? file : file?.id);

const resolveStaticFilesForDisplay = (configFiles = [], fileMap = {}) => (
  configFiles.map((file) => {
    if (typeof file === 'string') return fileMap[file];
    return file;
  }).filter((file) => !!file)
);

const preview = ({
  configProps = {},
  responses,
  id,
  setResponses,
  responding = false,
  sections,
  projectId,
  templateId,
  divisions,
  isDisplay,
  showCondensedView,
  onOpenFile,
  fileMap = {},
  onFieldTrigger,
  setCurrFileFieldId,
  setOpenFileIndex,
  setCurrFieldId,
  isExternalForm = false,
  isFormBuilder = false,
} = {}) => {
  const externalTemplateId = isExternalForm ? templateId : null;
  const isFieldDisplay = !isFormBuilder && isDisplay;
  const {
    title = 'Title goes here',
    fieldTrigger,
    files: configFiles = [],
    onClick,
  } = configProps;

  const {
    [id]: {
      files: responseFiles,
    } = {},
  } = responses ?? {};

  if (responding && setResponses && !responseFiles) {
    setResponses({
      ...responses,
      [id]: {
        files: configFiles?.map?.(resolveStaticFileId)
          .filter((fileId) => !!fileId),
      },
    });
  }

  const files = responding
    ? responseFiles?.map((fileId) => fileMap[fileId]).filter((file) => !!file) ?? []
    : resolveStaticFilesForDisplay(configFiles, fileMap);

  const onFileClick = (idx) => {
    const file = files[idx];
    if (!file) return;
    if (responding) {
      if (setOpenFileIndex) setOpenFileIndex(idx);
      if (setCurrFileFieldId) setCurrFileFieldId(id);
      if (setCurrFieldId) setCurrFieldId(id);
      if (onOpenFile) onOpenFile(file);
    } else if (onOpenFile) {
      onOpenFile(file);
    } else if (onClick) {
      onClick(idx);
    }
  };

  return (
    <>
      <TitleRow
        title={title}
        optional
        filter={
        fieldTrigger && !isDisplay
          ? (
            <FieldTriggerFlag
              sections={sections}
              templateId={templateId}
              projectId={projectId}
              divisions={divisions}
              configProps={configProps}
              responding={responding}
              onFieldTrigger={onFieldTrigger}
            />
          ) : null
      }
      />
      <div style={{ pointerEvents: 'all' }}>
        {showCondensedView
          ? (
            <PhotoReel
              small
              files={files}
              onClick={responding ? onFileClick : undefined}
              showCondensedView={showCondensedView}
            />
          )
          : (
            <FileTileView
              files={files}
              onClick={onFileClick}
              canDownload={responding || isFieldDisplay}
              externalTemplateId={externalTemplateId}
            />
          )}
      </div>
    </>
  );
};

const configure = ({
  setConfigProps,
  configProps = {},
  setCanSubmit,
  sections = [],
  isExternalForm,
  templateId,
  divisions,
  projectId,
  name,
  isBoardCards,
  setFieldTriggerEditable,
  showCondensedView,
  onShowFileModal,
  onOpenFile,
  id,
  customers = [],
  projects = [],
  users = [],
  costcodes = [],
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  formTemplates = [],
  labels = [],
  contactAddressBooks = {},
  buckets = [],
  scheduleCategories = [],
}) => {
  const {
    hasConditionalRendering = false,
    conditionalRenderingFormula,
  } = configProps;
  const files = configProps?.files ?? [];
  const setTitle = (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setConfigProps({
      ...configProps,
      title: value,
    });
    setCanSubmit(value);
  };

  const setConditionalRenderingFormula = (newValue) => {
    setConfigProps({
      ...configProps,
      conditionalRenderingFormula: newValue,
    });
  };

  const setHasConditionalRendering = (checked) => {
    setConfigProps({
      ...configProps,
      hasConditionalRendering: checked,
    });
  };

  const setFieldTrigger = (checked) => {
    setConfigProps({
      ...configProps,
      fieldTrigger: checked,
    });
  };

  const onAttachDelete = (idx) => {
    // set responses without the file in question
    const newFiles = [...files];
    newFiles.splice(idx, 1);
    setConfigProps({
      ...configProps,
      files: newFiles,
    });
  };

  const onFileClick = (idx) => {
    onOpenFile(files[idx]);
  };

  return (
    <div>
      <Row className="form-required-field">
        Text:
      </Row>
      <Row style={{ marginTop: 5 }}>
        <OnTraccrTextInput
          textarea
          placeholder="Insert title here"
          onChange={setTitle}
          value={configProps.title}
        />
      </Row>
      <ConditionalRenderingRow
        onChange={setHasConditionalRendering}
        onFormulaChange={setConditionalRenderingFormula}
        hasConditionalRendering={hasConditionalRendering}
        conditionalRenderingFormula={conditionalRenderingFormula}
        sections={sections}
        id={id}
        costcodes={costcodes}
        customers={customers}
        projects={projects}
        users={users}
        phases={phases}
        projectIdMap={projectIdMap}
        vendors={vendors}
        equipment={equipment}
        formTemplates={formTemplates}
        labels={labels}
        contactAddressBooks={contactAddressBooks}
        buckets={buckets}
        scheduleCategories={scheduleCategories}
      />
      {!isBoardCards && !isExternalForm && (
        <FieldTriggerCheckbox
          onChange={setFieldTrigger}
          onEditableChange={setFieldTriggerEditable}
          sections={sections}
          projectId={projectId}
          templateId={templateId}
          divisions={divisions}
          configProps={configProps}
          setConfigProps={setConfigProps}
          name={name}
        />
      )}
      <Row style={{ marginTop: 5 }}>
        <Col style={{ marginTop: 10, width: 60 }}>
          <Row style={{ height: 80 }} align="bottom">
            <Row style={{ height: 70 }} align="top">
              <Button
                className="form-attachment-add-button"
                icon={(
                  <FileAddOutlined
                    style={{
                      color: colors.ONTRACCR_RED,
                    }}
                  />
                  )}
                onClick={onShowFileModal}
              />
            </Row>
          </Row>
        </Col>
        <Col style={{ width: 'calc(100% - 60px)' }}>
          <PhotoReel
            small
            files={files}
            onDelete={onAttachDelete}
            onClick={onFileClick}
            showCondensedView={showCondensedView}
          />
        </Col>
      </Row>
    </div>
  );
};
export default {
  configure,
  preview,
  title: 'Static Attachments',
  description: 'Add files to display to users filling out the form.',
};
